import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, TemplateRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

import { Event } from '../../../models/event';
import { UiItem } from '../../../models/ui-item';

import { DatetimePickerComponent } from '../../common/datetime-picker/datetime-picker.component';

import { AuthService } from '../../../services/common/auth.service';

import { ContactService } from '../../../services/contacts/contact.service';
import { EventService } from '../../../services/events/event.service';
import { NotyService } from '../../../services/common/noty.service';
import { RruleService } from '../../../services/events/rrule.service';
import { setUTCOffset } from 'ngx-bootstrap/chronos/units/offset';

declare var moment: any;
declare var window: any;

@Component({
  selector: 'app-event-form',
  templateUrl: './event-form.component.html',
  styleUrls: ['./event-form.component.css'],
})
export class EventFormComponent implements OnInit, OnChanges {
  //TODO: Add query params from search params and vice versa. Matter index has the methods for that.

  /**
   * Event Id for edit
   */
  @Input() eventId: number | undefined;

  /**
   * Matter id for new form
   */
  @Input() matterId: number;

  @Input() eventDate: string;

  /**
   * Send form result to parent component
   * @param {EventEmitter<string> = new EventEmitter(} ) formResult variable
   */
  @Output() formResult: EventEmitter<string> = new EventEmitter();

  /**
   * Show event modal in parent component
   * @param {EventEmitter<number> = new EventEmitter(} ) showEvent variable
   */
  @Output() showEvent: EventEmitter<number> = new EventEmitter();

  /**
   * Addresses for selected contact
   * @type {Array<UiItem>}
   */
  addresses: Array<UiItem> = [];

  /**
   * Contact Id selected
   * @type {number}
   */
  contactId: number | undefined;

  eventDateUsed: boolean = false;

  /**
   * Event Object
   */
  eventObject: Event = new Event();

  /**
   * Id for event show modal
   * @type {number | undefined}
   */
  eventShowId: number | undefined;

  /**
   * Recurring event date picker component
   * @type {DatetimePickerComponent}
   */
  eventUntilTimeDatepicker: DatetimePickerComponent;

  /**
   * Form options with select field values and time zone offset
   * @type {any}
   */
  formOptions: any = {
    deliver_options: [],
    event_type_ids_phone: [],
    event_types: [],
  };

  formOptionsCheckInterval: any;

  /**
   * Indicates if form options are loaded
   * @type {boolean}
   */
  formOptionsLoaded: boolean = false;

  /**
   * Indicates if form has been submitted atleast once
   * @type {boolean}
   */
  formSubmitted: boolean = false;

  /**
   * Indicates if form is submitting data to backend
   * @type {boolean}
   */
  formSubmitting: boolean = false;

  /**
   * Involvements from selected matter as possible attendees
   * @type {Array<any>}
   */
  involvementAttendees: Array<any> = [];

  /**
   * All possible matter user attendees attendees list from users
   * @type {Array<UiItem>}
   */
  matterUserAttendees: Array<UiItem> = [];

  /**
   * Overlapping events for selected dates and attendees
   * @type {Array<any>}
   */
  overlappingEvents: Array<any> = [];

  /**
   * Indicates if the event is a phone event
   * @type {boolean}
   */
  phoneEvent: boolean = false;

  /**
   * List of phone numbers for selected contact
   * @type {Array<UiItem>}
   */
  phoneNumbers: Array<UiItem> = [];

  /**
   * Recurring rules object for rrule library (handled by rrule service)
   * @type {any}
   */
  recurring: any = {
    freq: 'NONE',
    interval: 1,
  };

  /**
   * Object to handle recurring days selection, translated to recurring object fields
   * @type {any}
   */
  recurringDays: any = {
    by_day: {},
    by_month_day: {},
    by_week_day: {},
  };

  /**
   * Indicates if the event is saving
   * @type {boolean}
   */
  saving: boolean = false;

  /**
   * Selected attendees count
   * @type {number}
   */
  selectedMatterUserAttendeesCount: number = 0;

  /**
   * Modal object for event show
   * @type {BsModalRef}
   */
   showModalRef: BsModalRef;

  /**
   * Indicates if tech debug information is to be shown
   * @type {boolean}
   */
  techDebug: boolean = false;

  /**
   * Event start date picker component
   * @param {[type]} 'eventStart' variable name
   */
  @ViewChild('eventStart', { static: true }) eventStartDatepicker: DatetimePickerComponent;

  /**
   * Event show template ref
   * @param {[type]} 'event_show' variable in HTML
   */
  @ViewChild('event_show', { static: true }) eventShowTemplateRef: TemplateRef<any>;

  /**
   * Event end date picker component
   * @param {[type]} 'eventEnd' variable name
   */
  @ViewChild('eventEnd', { static: true }) eventEndDatepicker: DatetimePickerComponent;

  /**
   * Event until time date picker component setter
   * @param {DatetimePickerComponent} 'eventUntilTime') set eventUntilTime(eventUntilTimeComponent setter function
   */
  @ViewChild('eventUntilTime') set eventUntilTime(eventUntilTimeComponent: DatetimePickerComponent) {
    this.eventUntilTimeDatepicker = eventUntilTimeComponent;
  }

  /**
   * Constructor
   * @param {ContactService} private contactService Contact service to contact resource on the backend
   * @param {EventService}   private eventService   Event service to event resource on the backend
   * @param {BsModalService} private modalService Modal service from ngx-bootstrap
   */
  constructor(private authService: AuthService, private contactService: ContactService, private eventService: EventService, private modalService: BsModalService, private notyService: NotyService, public rruleService: RruleService) {
    this.rruleService.setVariables();
    this.authService.techDebug().then((techDebug) => {
      this.techDebug = techDebug;
    });
  }

  // Component lifecycle methods

  /**
   * Called when the component is initialized
   */
  ngOnInit() {
    this.eventService.formOptions().then((response_json) => {
      this.formOptions = response_json;
      console.log('Form options loaded');
      if (!this.eventDate && !this.eventId) this.newEvent();
      this.formOptionsLoaded = true;
    });
  }

  /**
     * Called when component is destroyed
     */
  ngOnDestroy() {
    if (this.showModalRef && this.showModalRef.hide) this.showModalRef.hide();
  }

  /**
   * Called when the component variables change
   * @param {SimpleChanges} changes Angular simple changes containing variables which have changed with the current value
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.eventId) {
      if (changes.eventId.currentValue) {
        this.editEvent();
      } else if (this.formOptionsLoaded) this.newEvent();
    }
    if (changes.matterId) {
      console.log('Changes matter id');
      this.eventObject.matter_id = this.matterId;
      if (this.matterId) {
        if (!this.formOptionsLoaded) {
          let scope: EventFormComponent = this;
          this.formOptionsCheckInterval = window.setInterval(() => {
            if (this.formOptionsLoaded) {
              window.clearInterval(scope.formOptionsCheckInterval);
              scope.onMatterSelect(scope.matterId);
            }
          }, 200);
        } else this.onMatterSelect(this.matterId);
      }
    }
    if (changes.eventDate && changes.eventDate.currentValue && !this.eventDateUsed) {
      if (!this.formOptionsLoaded) {
        let scope: EventFormComponent = this;
        this.formOptionsCheckInterval = window.setInterval(() => {
          if (this.formOptionsLoaded) {
            window.clearInterval(scope.formOptionsCheckInterval);
            scope.newEvent();
          }
        }, 200);
      } else this.newEvent();
    }
  }

  // Component functions

  /**
   * Fix time to 7AM and 9PM when event is all day
   */
  allDayChanged() {
    if (this.eventObject.all_day) {
      let startDateStruct: any = this.eventObject.start_date_struct;
      let endDateStruct: any = this.eventObject.end_date_struct;
      let currentTime: any = moment().utcOffset(this.formOptions.timezone_offset);
      let startDate: any = moment([startDateStruct.year, startDateStruct.month - 1, startDateStruct.day, 7, 0, 0]); //.utcOffset(this.formOptions.timezone_offset);
      let endDate: any = moment([endDateStruct.year, endDateStruct.month - 1, endDateStruct.day, 21, 0, 0]); //.utcOffset(this.formOptions.timezone_offset);
      if (currentTime > startDate) {
        startDate = startDate.add(1, 'day');
        endDate = endDate.add(1, 'day');
      }
      this.eventStartDatepicker.setDate(startDate);
      this.eventEndDatepicker.setDate(endDate);
    }
  }

  /**
   * Build rrule for recurring events
   */
  buildRrule() {
    let ruleObject: any = this.rruleService.buildRrule(this.recurring, this.recurringDays);
    this.eventObject.rrule = ruleObject.string;
    this.eventObject.rrule_text = ruleObject.text;
  }

  /**
   * Build rrule objects
   */
  buildRruleObjects() {
    let rules: any = this.rruleService.buildRruleObjects(this.eventObject.rrule);
    this.eventObject.rrule_text = rules.rrule_text;
    if (rules.recurring) this.recurring = rules.recurring;
    if (rules.recurringDays) this.recurringDays = rules.recurringDays;
  }

  /**
   * Clear selected contact
   */
  clearContactSelection(initialLoad: boolean = false) {
    this.contactId = undefined;
    this.addresses = [];
    this.phoneNumbers = [];
    if (!initialLoad) {
      this.eventObject.address_id = undefined;
      this.eventObject.phone_number_id = undefined;
    }
  }

  /**
   * Clear selected matter
   */
  clearMatterSelection() {
    this.eventObject.matter_id = undefined;
    this.matterUserAttendees = [];
    this.involvementAttendees = [];
  }

  /**
   * Show event through parent component
   * @param {number} id Id of event to show
   */
  displayEvent(id: number) {
    this.showEvent.emit(id);
  }

  /**
   * Edit event
   */
  editEvent() {
    if (this.eventId) {
      this.eventService
        .edit(this.eventId)
        .then((response_json) => {
          if (response_json.success === false) {
            this.notyService.notify(response_json.text || 'Error editing the event', 'error');
            this.formResult.emit('cancelled');
          } else {
            let scope: EventFormComponent = this;
            this.eventObject = response_json.event;
            let selectedMatterUserAttendeesCount: number = 0;
            this.onMatterSelect(this.eventObject.matter_id, true).then(() => {
              if (this.eventObject.contact_id)
                window.setTimeout(() => {
                  scope.onContactSelect(this.eventObject.contact_id, true);
                  for (let attendee of scope.matterUserAttendees)
                    if (scope.eventObject.matter_user_attendee_ids.indexOf(attendee.value) > -1) {
                      attendee.checked = true;
                      selectedMatterUserAttendeesCount += 1;
                    }
                  scope.selectedMatterUserAttendeesCount = selectedMatterUserAttendeesCount;
                  for (let attendee of scope.involvementAttendees) if (scope.eventObject.involvement_attendee_ids.indexOf(attendee.value) > -1) attendee.checked = true;
                }, 100);
            });
            this.eventObject.start_date = this.eventService.addOffset(moment(this.eventObject.start), this.eventObject.s_offset);
            this.eventObject.end_date = this.eventService.addOffset(moment(this.eventObject.end), this.eventObject.e_offset);
            this.eventStartDatepicker.setDate(this.eventObject.start_date);
            this.eventEndDatepicker.setDate(this.eventObject.end_date);
            if (this.eventObject.reminder && this.eventObject.reminder > 0) {
              this.eventObject.reminder_required = true;
              if (this.eventObject.reminder % 86400 == 0) {
                this.eventObject.reminder_duration_unit = 86400;
                this.eventObject.reminder_duration = this.eventObject.reminder / 86400;
              } else if (this.eventObject.reminder % 3600 == 0) {
                this.eventObject.reminder_duration_unit = 3600;
                this.eventObject.reminder_duration = this.eventObject.reminder / 3600;
              } else if (this.eventObject.reminder % 60 == 0) {
                this.eventObject.reminder_duration_unit = 60;
                this.eventObject.reminder_duration = this.eventObject.reminder / 60;
              } else {
                this.eventObject.reminder_duration_unit = 60;
                this.eventObject.reminder_duration = 5;
              }
            } else {
              this.eventObject.reminder_required = false;
              this.eventObject.reminder_duration_unit = 60;
              this.eventObject.reminder_duration = 1;
            }
            if (this.eventObject.rrule) {
              let rruleObjects: any = this.rruleService.buildRruleObjects(this.eventObject.rrule);
              this.eventObject.rrule_text = rruleObjects.rrule_text;
              this.recurring = rruleObjects.recurring;
              this.recurringDays = rruleObjects.recurringDays;
            }
            if (this.eventObject.rrule) {
              if (this.eventObject.until_time) {
                this.eventObject.repeats_forever = false;
                this.eventObject.until_time_date = this.eventService.addOffset(moment(this.eventObject.until_time), this.eventObject.u_offset);
                let scope: EventFormComponent = this;
                if (!this.eventUntilTimeDatepicker)
                  window.setTimeout(() => {
                    scope.eventUntilTimeDatepicker.setDate(scope.eventObject.until_time_date);
                  }, 500);
                else this.eventUntilTimeDatepicker.setDate(this.eventObject.until_time_date);
              } else this.eventObject.repeats_forever = true;
            }
            for (let deliveryOption of this.formOptions.deliver_options) if (this.eventObject.reminder_delivery_methods.indexOf(deliveryOption.value) > -1) deliveryOption.checked = true;
            this.onEventTypeChange();
          }
        })
        .catch((err) => {
          console.error(err);
          this.notyService.notify('Error editing the event', 'error');
          this.formResult.emit('cancelled');
        });
    }
  }

  /**
   * Checks if form data is valid
   * @return {boolean} Indicates if form is valid
   */
  formValid(): boolean {
    let eventObject: any = this.eventObject;
    if (
      eventObject.event_type_id &&
      eventObject.matter_id &&
      ((this.phoneEvent && eventObject.phone_number_id) || (!this.phoneEvent && eventObject.address_id)) &&
      (eventObject.intranet_attendee || this.matterUserAttendeeIds().length > 0) &&
      (!this.recurring || this.recurring.freq == 'NONE' || eventObject.repeats_forever || eventObject.until_time_struct)
    )
      return true;
    else return false;
  }

  /**
   * Get Involvement Attendee ids from attendees
   * @return {Array<number>} [description]
   */
  involvementAttendeeIds(): Array<number> {
    let attendeeIds: Array<number> = [];
    for (let attendee of this.involvementAttendees) if (attendee.checked) attendeeIds.push(attendee.value);
    return attendeeIds;
  }

  /**
   * Load overlapping events
   */
  loadOverlappingEvents() {
    this.eventService
      .overlappingEvents({
        attendee_ids: this.matterUserAttendeeIds(),
        end: this.eventService.moment(this.eventObject.end_date_struct).format(),
        start: this.eventService.moment(this.eventObject.start_date_struct).format(),
      })
      .then((response_json) => {
        if (response_json) {
          this.overlappingEvents = response_json;
          for (let event of this.overlappingEvents) event.formattedDateTitle = this.eventService.formatEventTitleFullDate(this.eventService.strToMoment(event.start, null, event.s_offset), this.eventService.strToMoment(event.end, null, event.e_offset), true, false, true);
        }
      });
  }

  /**
   * Get Matter User Attendee ids from attendees
   * @return {Array<number>} [description]
   */
  matterUserAttendeeIds(): Array<number> {
    let attendeeIds: Array<number> = [];
    for (let attendee of this.matterUserAttendees) if (attendee.checked) attendeeIds.push(attendee.value);
    return attendeeIds;
  }

  /**
   * Called to initialize the form fields for a new event
   */
  newEvent() {
    this.addresses = [];
    this.matterUserAttendees = [];
    this.eventObject = new Event();
    this.involvementAttendees = [];
    this.overlappingEvents = [];
    this.phoneEvent = false;
    this.phoneNumbers = [];
    this.recurring = {
      freq: 'NONE',
      interval: 1,
    };
    this.recurringDays = {
      by_day: {},
      by_month_day: {},
      by_week_day: {},
    };
    this.selectedMatterUserAttendeesCount = 0;
    let current_time: any = moment();
    let start_time: any = moment();
    let end_time: any = moment();
    if (this.eventDate && !this.eventDateUsed) {
      console.log('new event with date');
      console.log(this.eventDate);
      let offset: number = this.formOptions.timezone_offset;
      let offset_str: string = `${offset < 0 ? '-' : '+'}${offset / 60 < 10 ? '0' : ''}${Math.abs(offset / 60)}:${Math.abs(offset % 60)}${Math.abs(offset % 60) < 10 ? '0' : ''}`;
      start_time = this.eventService.addOffset(moment(this.eventDate + 'T00:00:00' + offset_str), this.formOptions.timezone_offset);
      end_time = this.eventService.addOffset(moment(this.eventDate + 'T00:00:00' + offset_str), this.formOptions.timezone_offset);
      console.log(start_time, end_time);
      this.eventDateUsed = true;
    }
    current_time.utcOffset(this.formOptions.timezone_offset);
    start_time.utcOffset(this.formOptions.timezone_offset);
    end_time.utcOffset(this.formOptions.timezone_offset);
    start_time
      .hour(current_time.hour())
      .minute(current_time.minute() > 30 ? 30 : 0)
      .utcOffset(this.formOptions.timezone_offset);
    end_time
      .hour(current_time.minute() > 30 ? current_time.hour() + 1 : current_time.hour())
      .minute(current_time.minute() > 30 ? 0 : 30)
      .utcOffset(this.formOptions.timezone_offset);
    this.eventStartDatepicker.setDate(start_time);
    this.eventEndDatepicker.setDate(end_time);
    if (!this.eventId) this.eventObject.event_type_id = this.formOptions.event_types[0].value;
    if (this.matterId) this.eventObject.matter_id = this.matterId;
  }

  /**
   * Attendee check change
   */
  onAttendeeCheck() {
    let selectedMatterUserAttendeesCount: number = 0;
    for (let attendee of this.matterUserAttendees) if (attendee.checked) selectedMatterUserAttendeesCount += 1;
    if (this.eventObject.intranet_attendee) selectedMatterUserAttendeesCount += 1;
    this.selectedMatterUserAttendeesCount = selectedMatterUserAttendeesCount;
    this.loadOverlappingEvents();
  }

  /**
   * Process contact select
   * @param {any} contactId Id of contact selected
   */
  onContactSelect(contactId: number, initialLoad: boolean = false) {
    this.contactId = contactId;
    this.contactService.findAddress(this.contactId).then((response_json) => {
      this.addresses = response_json.addresses;
      if (!initialLoad && this.addresses && this.addresses.length > 0) this.eventObject.address_id = this.addresses[0].value;
    });
    this.contactService.findPhone(this.contactId).then((response_json) => {
      this.phoneNumbers = response_json.phone_numbers;
      if (!initialLoad && this.phoneNumbers && this.phoneNumbers.length > 0) this.eventObject.phone_number_id = this.phoneNumbers[0].value;
    });
  }

  /**
   * Process event type change
   */
  onEventTypeChange() {
    if (this.eventObject.event_type_id) {
      if (this.formOptions.event_type_ids_phone.indexOf(Number(this.eventObject.event_type_id)) > -1) this.phoneEvent = true;
      else this.phoneEvent = false;
    } else this.phoneEvent = false;
  }

  /**
   * Process matter select
   * @param {any} matterId Id of selected matter
   */
  onMatterSelect(matterId: any, initialLoad: boolean = false): Promise<any> {
    console.log('Matter select called', matterId, initialLoad);
    if (this.eventObject.matter_id != matterId) this.eventObject.matter_id = matterId;
    return this.eventService.fetchMatterInfo(matterId).then((response_json) => {
      this.matterUserAttendees = response_json.attendees;
      this.involvementAttendees = response_json.involvements;
      if (!initialLoad) {
        this.eventObject.address_id = response_json.address_id;
        this.eventObject.phone_number_id = response_json.phone_number_id;
      }
      if (!initialLoad && response_json.contact_id) this.onContactSelect(response_json.contact_id, initialLoad);
      else this.clearContactSelection(initialLoad);
    });
  }

  /**
     * Open Show Modal
     * @param {TemplateRef<any>} template Template to show
     * @param {false         }}  config   Config for Bs Modal with a default value
     */
  openShowModal(template: TemplateRef<any>, config: any = { backdrop: true, ignoreBackdropClick: true }) {
    if (this.showModalRef) this.showModalRef.hide();
    this.showModalRef = this.modalService.show(template, config);
  }

  /**
   * Set delivery methods
   */
  setDeliveryMethods() {
    this.eventObject.reminder_delivery_methods = [];
    for (let deliveryOption of this.formOptions.deliver_options) if (deliveryOption.checked) this.eventObject.reminder_delivery_methods.push(deliveryOption.value);
  }

  /**
   * Save Event
   */
  save(formResult: string = 'saved') {
    this.formSubmitted = true;
    if (this.formValid()) {
      this.eventObject.start = this.eventService.moment(this.eventObject.start_date_struct).format('YYYY-MM-DDTHH:mm:ss');
      this.eventObject.end = this.eventService.moment(this.eventObject.end_date_struct).format('YYYY-MM-DDTHH:mm:ss');
      if (this.eventObject.until_time_struct) {
        try {
          this.eventObject.until_time = this.eventService.moment(this.eventObject.until_time_struct).format('YYYY-MM-DDTHH:mm:ss');
        } catch (err) {
          console.error('Until time moment parse error with struct', err);
        }
      }
      this.eventObject.involvement_attendee_ids = this.involvementAttendeeIds();
      this.eventObject.matter_user_attendee_ids = this.matterUserAttendeeIds();
      if (this.eventObject.reminder_required) this.eventObject.reminder = (this.eventObject.reminder_duration || 1) * (this.eventObject.reminder_duration_unit || 60);
      else this.eventObject.reminder = undefined;
      this.saving = true;
      this.eventService
        .save(this.eventObject)
        .then((response_json) => {
          this.saving = false;
          if (response_json.success) {
            this.notyService.notify(response_json.text || 'Event saved', 'success', 5000, 'center');
            this.formResult.emit(formResult);
            if(formResult === "save_view"){
              this.eventShowId = response_json.id;
              this.openShowModal(this.eventShowTemplateRef);
            }
          } else this.notyService.notify(response_json.error || response_json.text || 'Error saving events', 'error', 5000, 'center');
        })
        .catch((err: any) => {
          this.saving = false;
          const errorMsg = err?.error?.error || err?.error?.text || 'Error saving events';
          this.notyService.notify(errorMsg, 'error', 5000, 'center');
        });
    } else this.notyService.notify('Please check form again', 'warning', 5000, 'center');
  }

  setDefaultEndDate(struct: any) {
    struct = Object.assign({}, struct);
    if (struct.hour) {
      struct.minute += 30;
      if (struct.minute > 59) {
        struct.hour += 1;
        struct.minute = struct.minute % 60;
        if (struct.hour > 23) {
          struct.day += 1;
        }
      }
      this.eventObject.end_date_struct = struct;
    }
  }
}
